import React, { useMemo } from "react";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import { useTranslation } from "next-i18next";
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Switch,
  SimpleGrid,
  useDisclosure,
  Button,
  Collapse,
  Heading,
  Text,
} from "@chakra-ui/react";
import validator from "validator";

import BoxCard from "@/components/common/BoxCard";
import { AccreditationTypeFields } from "@/types/event";
import ChooseAvatarModal from "@/components/common/ChooseAvatarModal";
import { Image } from "@/components/common/Image";
import FileInput from "@/components/common/FileInput";
import { ImageAutoHeight } from "@/components/common/Image";
import QuillEditor from "@/components/common/QuillEditor";
import { useUser } from "@/hooks/useUser";

const PESEL_REGEX = /^\d{11}$/;

const AccreditationFieldsComponent = ({
  rounded = true,
}: {
  rounded?: boolean;
}): React.ReactElement => {
  const { t } = useTranslation("common");
  const [session] = useUser();
  const {
    isOpen: isAvatarModalOpen,
    onOpen: onAvatarModalOpen,
    onClose: onAvatarModalClose,
  } = useDisclosure();

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const { fields: journalistFields } = useFieldArray({
    name: "accreditation.fields",
    control,
  });

  const accreditationFields = watch("accreditation.fields", journalistFields);

  const data = useMemo(
    () =>
      journalistFields.reduce(
        (fields, field) => ({
          ...fields,
          [field.id]: field,
        }),
        {}
      ),
    [journalistFields]
  );

  return (
    <>
      {accreditationFields?.length > 0 && (
        <>
          {accreditationFields?.map((field, index) => {
            if (!field?.accreditationField) return null;
            let label = null;
            let controller = null;
            const formError = (
              <>
                {errors?.accreditation?.fields?.[index]?.value && (
                  <FormErrorMessage>
                    {t("users.fieldRequired")}
                  </FormErrorMessage>
                )}
              </>
            );

            label = (
              <FormLabel
                pb={0}
                fontWeight="inherit"
                fontSize="inherit"
                lineHeight="inherit"
              >
                {t(`events.${field.accreditationField?.name?.en}`)}
              </FormLabel>
            );

            if (
              field.accreditationField?.type === AccreditationTypeFields.FILE &&
              field.accreditationField?.name?.en === "photo"
            ) {
              controller = (
                <Controller
                  control={control}
                  name={`accreditation.fields.${index}.value`}
                  rules={{
                    validate: (value) =>
                      !!(
                        !field.accreditationField?.required ||
                        value ||
                        data?.[field.id]?.value
                      ),
                  }}
                  render={({ field: { onChange, value, ref } }) => (
                    <>
                      {field.accreditationField?.default &&
                      field.accreditationField?.name?.en === "photo" ? (
                        <>
                          <Collapse in={!!value} animateOpacity>
                            {value && (
                              <Box
                                w={100}
                                h={100}
                                mb={2}
                                position="relative"
                                borderRadius="50%"
                                overflow="hidden"
                              >
                                <Image
                                  src={
                                    value instanceof File
                                      ? URL.createObjectURL(value)
                                      : `/uploads/${value?.filename}`
                                  }
                                  alt=""
                                  objectFit="cover"
                                  unoptimized
                                  quality={90}
                                />
                              </Box>
                            )}
                          </Collapse>
                          <Box>
                            <Button
                              onClick={onAvatarModalOpen}
                              size="sm"
                              flexShrink={0}
                              ref={ref}
                            >
                              {t("chooseFile")}
                            </Button>
                            {value && (
                              <Button
                                onClick={() => onChange(null)}
                                size="sm"
                                flexShrink={0}
                                variant="red"
                                ml={{ base: 0, md: 2 }}
                                mt={{ base: 2, md: 0 }}
                              >
                                {t("delete")}
                              </Button>
                            )}
                            {isAvatarModalOpen && (
                              <ChooseAvatarModal
                                onChange={(file) => onChange(file)}
                                onClose={onAvatarModalClose}
                              />
                            )}
                          </Box>
                        </>
                      ) : (
                        <FileInput
                          name="file"
                          value={
                            value?.filename ??
                            value ??
                            data?.[field.id]?.value?.filename
                          }
                          onChange={(file) => onChange(file)}
                        />
                      )}
                    </>
                  )}
                />
              );
              return (
                <BoxCard
                  p={0}
                  mt={3}
                  borderRadius={rounded ? ".375rem" : 0}
                  border={
                    !!errors?.accreditation?.fields?.[index]?.value
                      ? "1px"
                      : undefined
                  }
                  borderStyle="solid"
                  borderColor="red"
                >
                  <FormControl
                    isRequired={field.accreditationField?.required}
                    mb={2}
                    key={index}
                    isInvalid={!!errors?.accreditation?.fields?.[index]?.value}
                  >
                    <Box
                      p="1.25rem"
                      borderBottomColor="light.50"
                      borderBottomWidth="1px"
                    >
                      <Heading as="h3" size="md">
                        {label}
                      </Heading>
                    </Box>
                    <SimpleGrid
                      columns={{ base: 1, md: 2 }}
                      spacingX={5}
                      spacingY={3}
                      p="1.25rem"
                    >
                      <Box fontStyle="italic">
                        <Box
                          w={{ base: "100%", md: "150px" }}
                          float="left"
                          mr={{ base: 0, md: 4 }}
                          mb={{ base: 4, md: 0 }}
                        >
                          <ImageAutoHeight
                            src="/photo-instruction.jpg"
                            layout="fill"
                            alt="Instruction"
                          />
                        </Box>
                        <Text>{t("events.photoInstruction1")}</Text>
                        <Text mt={2}>{t("events.photoInstruction2")}</Text>
                      </Box>
                      <Box>
                        {controller}
                        {formError}
                      </Box>
                    </SimpleGrid>
                  </FormControl>
                </BoxCard>
              );
            }
          })}
          <BoxCard p={0} mt={3} borderRadius={rounded ? ".375rem" : 0}>
            <Box p="1.25rem">
              <SimpleGrid spacing={5} columns={1}>
                {accreditationFields?.map((field, index) => {
                  if (field.accreditationField?.name?.en !== "photo") {
                    if (!field?.accreditationField) return null;
                    let label = null;
                    let controller = null;
                    let formError = (
                      <>
                        {errors?.accreditation?.fields?.[index]?.value && (
                          <FormErrorMessage>
                            {t("users.fieldRequired")}
                          </FormErrorMessage>
                        )}
                      </>
                    );

                    if (field?.accreditationField?.default) {
                      if (field.accreditationField?.name?.en === "address") {
                        label = null;
                        formError = null;
                      } else
                        label = (
                          <FormLabel>
                            {t(`events.${field.accreditationField?.name?.en}`)}
                          </FormLabel>
                        );
                    } else {
                      label = (
                        <FormLabel>
                          {field.accreditationField?.name?.[
                            session?.user?.profile?.locale
                          ] ??
                            field.accreditationField?.name?.en ??
                            field.accreditationField?.name[
                              Object.keys(field.accreditationField?.name)[0]
                            ]}
                        </FormLabel>
                      );
                    }

                    if (
                      field.accreditationField?.type ===
                      AccreditationTypeFields.TEXT
                    ) {
                      if (field.accreditationField?.name?.en === "address") {
                        controller = Object.keys(field.value)?.map(
                          (addressName) => (
                            <FormControl
                              key={addressName}
                              isRequired={field.accreditationField?.required}
                              isInvalid={
                                !!errors?.accreditation?.fields?.[index]?.value
                              }
                              mb={2}
                            >
                              <FormLabel>{t(`users.${addressName}`)}</FormLabel>
                              <Controller
                                control={control}
                                name={`accreditation.fields.${index}.value.${addressName}`}
                                rules={{
                                  required: field.accreditationField?.required,
                                }}
                                render={({
                                  field: { onChange, value, ref },
                                }) => (
                                  <Input
                                    onChange={onChange}
                                    value={value ?? ""}
                                    ref={ref}
                                  />
                                )}
                              />
                              {errors?.accreditation?.fields?.[index]
                                ?.value && (
                                <FormErrorMessage>
                                  {t("users.fieldRequired")}
                                </FormErrorMessage>
                              )}
                            </FormControl>
                          )
                        );
                      } else if (
                        field.accreditationField?.name?.en === "phone"
                      ) {
                        controller = (
                          <Controller
                            control={control}
                            name={`accreditation.fields.${index}.value`}
                            rules={{
                              required: field.accreditationField?.required,
                              validate: (value) =>
                                (!field.accreditationField?.required &&
                                  !value) ||
                                validator.isMobilePhone(`${value}`, "any", {
                                  strictMode: true,
                                }) ||
                                t("users.enterCorrectPhone"),
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Input
                                onChange={onChange}
                                value={value ?? ""}
                                ref={ref}
                              />
                            )}
                          />
                        );
                        formError = (
                          <>
                            {errors?.accreditation?.fields?.[index]?.value && (
                              <FormErrorMessage>
                                {errors?.accreditation?.fields?.[index]?.value
                                  .type === "required"
                                  ? t("users.fieldRequired")
                                  : errors?.accreditation?.fields?.[index]
                                      ?.value.message}
                              </FormErrorMessage>
                            )}
                          </>
                        );
                      } else if (
                        field.accreditationField?.name?.en === "pesel"
                      ) {
                        controller = (
                          <Controller
                            control={control}
                            name={`accreditation.fields.${index}.value`}
                            rules={{
                              required: field.accreditationField?.required,
                              pattern: {
                                value: PESEL_REGEX,
                                message: t("users.enterCorrectPesel"),
                              },
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Input
                                onChange={onChange}
                                value={value ?? ""}
                                ref={ref}
                              />
                            )}
                          />
                        );
                        formError = (
                          <>
                            {errors?.accreditation?.fields?.[index]?.value && (
                              <FormErrorMessage>
                                {errors?.accreditation?.fields?.[index]?.value
                                  .type === "required"
                                  ? t("users.fieldRequired")
                                  : errors?.accreditation?.fields?.[index]
                                      ?.value.message}
                              </FormErrorMessage>
                            )}
                          </>
                        );
                      } else if (
                        field?.accreditationField?.name?.en === "publications"
                      ) {
                        controller = (
                          <Controller
                            control={control}
                            name={`accreditation.fields.${index}.value`}
                            rules={{
                              required: field.accreditationField?.required,
                            }}
                            render={({ field: { onChange, value, ref } }) => {
                              return (
                                <QuillEditor
                                  isInvalid={
                                    !!errors?.accreditation?.fields?.[index]
                                      ?.value
                                  }
                                  hideCloudIcons
                                  ref={ref}
                                  value={value || ""}
                                  onChange={(content, _, __, editor) =>
                                    onChange(
                                      editor.getLength() > 1 ? content : null
                                    )
                                  }
                                />
                              );
                            }}
                          />
                        );
                      } else {
                        controller = (
                          <Controller
                            control={control}
                            name={`accreditation.fields.${index}.value`}
                            rules={{
                              required: field.accreditationField?.required,
                            }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Input
                                onChange={onChange}
                                value={value ?? ""}
                                ref={ref}
                              />
                            )}
                          />
                        );
                      }
                    }

                    if (
                      field.accreditationField?.type ===
                      AccreditationTypeFields.CHECKBOX
                    ) {
                      controller = (
                        <Controller
                          control={control}
                          name={`accreditation.fields.${index}.value`}
                          render={({ field: { onChange, value, ref } }) => (
                            <Switch
                              onChange={onChange}
                              isChecked={value}
                              ml={2}
                              ref={ref}
                            />
                          )}
                        />
                      );
                    }

                    if (
                      field.accreditationField?.type ===
                      AccreditationTypeFields.FILE
                    ) {
                      controller = (
                        <Controller
                          control={control}
                          name={`accreditation.fields.${index}.value`}
                          rules={{
                            validate: (value) =>
                              !!(
                                !field.accreditationField?.required ||
                                value ||
                                data?.[field.id]?.value
                              ),
                          }}
                          render={({ field: { onChange, value, ref } }) => (
                            <>
                              {field.accreditationField?.default &&
                              field.accreditationField?.name?.en === "photo" ? (
                                <>
                                  <Collapse in={!!value} animateOpacity>
                                    {value && (
                                      <Box
                                        w={100}
                                        h={100}
                                        mb={2}
                                        position="relative"
                                        borderRadius="50%"
                                        overflow="hidden"
                                      >
                                        <Image
                                          src={
                                            value instanceof File
                                              ? URL.createObjectURL(value)
                                              : `/uploads/${value?.filename}`
                                          }
                                          alt=""
                                          objectFit="cover"
                                          unoptimized
                                          quality={90}
                                        />
                                      </Box>
                                    )}
                                  </Collapse>
                                  <Box>
                                    <Button
                                      onClick={onAvatarModalOpen}
                                      size="sm"
                                      flexShrink={0}
                                      ref={ref}
                                    >
                                      {t("chooseFile")}
                                    </Button>
                                    {value && (
                                      <Button
                                        onClick={() => onChange(null)}
                                        size="sm"
                                        flexShrink={0}
                                        variant="red"
                                        ml={2}
                                      >
                                        {t("delete")}
                                      </Button>
                                    )}
                                    {isAvatarModalOpen && (
                                      <ChooseAvatarModal
                                        onChange={(file) => onChange(file)}
                                        onClose={onAvatarModalClose}
                                      />
                                    )}
                                  </Box>
                                </>
                              ) : (
                                <FileInput
                                  name="file"
                                  value={
                                    value?.filename ??
                                    value ??
                                    data?.[field.id]?.value?.filename
                                  }
                                  onChange={(file) => onChange(file)}
                                  ref={ref}
                                />
                              )}
                            </>
                          )}
                        />
                      );
                    }

                    return (
                      <FormControl
                        isRequired={field.accreditationField?.required}
                        mb={2}
                        key={index}
                        isInvalid={
                          !!errors?.accreditation?.fields?.[index]?.value
                        }
                      >
                        {label}
                        {controller}
                        {formError}
                      </FormControl>
                    );
                  }
                })}
              </SimpleGrid>
            </Box>
          </BoxCard>
        </>
      )}
    </>
  );
};

export default AccreditationFieldsComponent;
